import ButtonGroup from '@mui/material/ButtonGroup';
import Link from '@mui/material/Link';
import * as React from 'react';
import type { CSSProperties } from 'react';

import Button from '~/common/mui/Buttons/MUIButton/MUIButton';

type Option = {
  value: string;
  label: React.ReactNode;
  href?: string;
  target?: string;
  rel?: string;
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
  sx?: CSSProperties;
};

type ToggleButtonGroupProps = {
  id?: string;
  options: Option[];
  value?: string;
  onChange?: (value: string) => void;
  size?: 'small' | 'medium' | 'large';
  disabled?: boolean;
};

const ToggleButtonGroup: React.FC<ToggleButtonGroupProps> = ({
  id,
  options,
  value,
  onChange,
  size = 'medium',
  disabled = false,
}) => (
  <ButtonGroup id={id} size={size} disabled={disabled}>
    {options.map((opt) => {
      const isSelected = value === opt.value;
      // If href is present, render as a link
      if (opt.href) {
        return (
          <Link
            key={opt.value}
            href={opt.href}
            target={opt.target || '_blank'}
            rel={opt.rel || 'noopener noreferrer'}
            underline="none"
            sx={{
              borderRadius: '8px',
              boxShadow: 'unset',
              padding: '0.375rem 1rem',
              display: 'inline-flex',
              alignItems: 'center',
              background: isSelected
                ? 'rgba(25, 118, 210, 0.08)'
                : 'transparent',
              border: '1px solid palette.blue[300]',
              color: isSelected ? 'palette.blue[600]' : 'inherit',
              fontWeight: isSelected ? 600 : 400,
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                background: 'rgba(25, 118, 210, 0.12)',
                textDecoration: 'none',
              },
              marginRight: 1,
              ...opt.sx,
            }}
            aria-pressed={isSelected}
            onClick={opt.onClick}
          >
            {opt.label}
          </Link>
        );
      }
      // Otherwise, render as a button
      return (
        <Button
          key={opt.value}
          variant={isSelected ? 'contained' : 'outlined'}
          onClick={
            opt.onClick ? opt.onClick : () => onChange && onChange(opt.value)
          }
          aria-pressed={isSelected}
          sx={{
            borderRadius: '8px',
            boxShadow: 'unset',
            padding: '0.375rem 1rem',
            ...opt.sx,
          }}
          disabled={opt.disabled}
        >
          {opt.label}
        </Button>
      );
    })}
  </ButtonGroup>
);

export default ToggleButtonGroup;
